<template>
  <div class="field search-container"
    :v-tooltip="{ content: 'Search', placement: 'bottom', classes: 'fab-tooltip' }">
    <div class="search-input-wrapper"
      :class="{ 'is-dark': $theme.isDark }">
      <span class="search-icon material-symbols-rounded has-text-primary">
        search
      </span>
      <bulma-autocomplete id="global_search"
        ref="searchInput"
        placeholder="Search the system..."
        v-model="searchQuery"
        :data="groupedResults"
        group-field="groupLabel"
        group-options="items"
        field=""
        :loading="isLoading"
        :open-on-focus="true"
        :loading-text="'Searching...'"
        @select="onSelectResult"
        @focus="onSearchFocus"
        @blur="onSearchBlur"
        class="global-search-autocomplete"
        :class="[$theme.name]">
        <template slot-scope="props">
          <global-search-quote-card :result-type="getResultType(props.option)"
            :assessor="props.option.assessorName"
            :claim-no="props.option.claimNo"
            :color="props.option.colour"
            :cost="props.option.totalIncGST"
            :customer-name="props.option.customer"
            :quote-id="props.option.id"
            :quote-number="props.option.quoteNo"
            :pos="props.option.pos"
            :invoices="props.option.invoices"
            :rego="props.option.regoNo"
            :insurer="props.option.insurer"
            :build-year="props.option.buildYear"
            :make-model="props.option.makeModel"
            :high-light="props.option.highlight"
            :phone-no="props.option.phoneNo"
            :job-stage="props.option.jobStage"
            :job-stages-sub-stages="jobStagesSubStages"
            :order-status="props.option.orderStatus"
            :deleted="props.option.deleted === true" />
        </template>
        <template slot="group"
          slot-scope="props">
          <div class="s-group-header">
            <span class="s-group-label">
              <span class="material-symbols-rounded s-group-icon">{{ getGroupIcon(props.group) }}</span>
              {{ props.group }}
            </span>
          </div>
        </template>
        <template slot="empty">
          <!-- loading -->
          <div class="has-text-centered has-text-grey"
            v-if="isLoading">
            <global-search-skeleton-card v-for="n in 3"
              :key="n" />
          </div>
          <!-- not loading -->
          <div class="has-text-centered has-text-grey"
            v-else-if="!isLoading && searchQuery && totalResultsCount === 0">
            No results found
          </div>
          <div class="has-text-centered has-text-grey"
            v-else>
            <div class="mb-3">
              Search for quotes, customers, vehicles, etc.
            </div>

            <!-- Beta information when focused -->
            <div v-if="isSearchFocused"
              class="beta-info-panel p-3"
              :class="$theme.name === 'dark' ? 'has-text-white' : 'has-text-black'">
              <!-- <div class="is-flex is-align-items-center is-justify-content-center mb-2">
                <span class="material-symbols-outlined mr-2">info</span>
                <span class="has-text-weight-semibold">Global Search is in Beta</span>
              </div>
              <div class="is-size-7 has-text-left">
                <p class="mb-2">
                  This feature is currently in beta.<br>We're actively enhancing it based on your feedback.
                </p>
                <p class="mb-2">
                  <strong>Current limitations:</strong>
                </p>
                <ul class="ml-3">
                  <li>• Searches limited to data from the last 6 months</li>
                </ul>
                <p class="mt-2 is-size-7">
                  💡 Have suggestions? Contact our Support team.
                </p>
              </div> -->
            </div>
          </div>
        </template> </bulma-autocomplete>
      <button @click="clearSearch"
        class="clear-search-button"
        type="button"
        aria-label="Clear search"
        title="Clear search">
        ×
      </button>
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import typesenseSearchService from './TypesenseSearchService'
import GlobalSearchQuoteCard from './GlobalSearchQuoteCard.vue'
import BulmaAutocomplete from '../BulmaAutocomplete/BulmaAutocomplete.vue'
// import Circle8 from '../Spinners/Circle8.vue'
import GlobalSearchSkeletonCard from './GlobalSearchSkeletonCard.vue'
import routes from '../../views/quote/routes'
import QuoteService from '../../views/quote/QuoteService'
import { latestOnly, CANCELLED } from '@/utils/AsyncFunctions'

export default {
  name: 'SearchBar',
  components: {
    GlobalSearchQuoteCard,
    BulmaAutocomplete,
    GlobalSearchSkeletonCard
    // Circle8
  },
  props: {
    scopedApiKey: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      searchQuery: '',
      isLoading: false,
      searchResults: [],
      jobStagesSubStages: [],
      timeout: null,
      showShortcutNotification: false,
      isSearchFocused: false
    }
  },
  computed: {
    routes() {
      return routes
    },
    companyId() {
      return this.$company ? this.$company.setting.companyId : null
    },
    quoteResults() {
      return this.searchResults.filter((item) => this.getResultType(item) === 'quote')
    },
    poResults() {
      return this.searchResults.filter((item) => this.getResultType(item) === 'po')
    },
    invoiceResults() {
      return this.searchResults.filter((item) => this.getResultType(item) === 'invoice')
    },
    totalResultsCount() {
      return this.searchResults.length
    },
    groupedResults() {
      const groups = [
        { groupLabel: `Quotes (${this.quoteResults.length})`, items: this.quoteResults },
        { groupLabel: `Purchase Orders (${this.poResults.length})`, items: this.poResults },
        { groupLabel: `Invoices (${this.invoiceResults.length})`, items: this.invoiceResults }
      ]

      return groups.filter((group) => group.items.length > 0)
    }
  },
  watch: {
    searchQuery(newQuery) {
      // Trigger the search when the query changes
      this.onSearchInput(newQuery)
    },
    searchResults(newResults) {
      // Handle any additional logic when search results change
      if (newResults.length > 0 && !this.isLoading && this.searchQuery == '') {
        this.searchResults = [] // Clear results if searchQuery is empty
      }
    },
    scopedApiKey: {
      handler(newApiKey) {
        if (newApiKey) {
          typesenseSearchService.initialize(newApiKey)
        }
      },
      immediate: true
    }
  },
  created() {
    // Only the latest search counts: starting a new one cancels the previous
    // request, so a slow, older response can't overwrite newer results.
    // Created here rather than in data() so Vue doesn't make it reactive.
    this.runSearch = latestOnly((signal, query) => typesenseSearchService.performSearch(query, { signal }))
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyPress)
    this.getJobStages()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyPress)
    clearTimeout(this.timeout)
    this.runSearch.cancel()
  },
  methods: {
    async getJobStages() {
      // Fetch job stages from the API or service
      const jobStagesAndSubStages = await QuoteService.getJobNSubJobStages()
      // Store the job stages in the component's data
      this.jobStagesSubStages = jobStagesAndSubStages
      // Return the job stages for use in the template or other methods
      return this.jobStages
    },
    handleKeyPress(event) {
      // Focus search with "/" key
      if (event.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        event.preventDefault()
        this.focusSearchInput()
      }
      // Clear search with Escape key when search is focused
      if (event.key === 'Escape' && document.activeElement && document.activeElement.closest('.global-search-autocomplete')) {
        this.clearSearch()
      }
    },
    focusSearchInput() {
      // Focus on the search input
      this.$nextTick(() => {
        const searchInput = this.$refs.searchInput
        if (searchInput && searchInput.$refs.input) {
          searchInput.$refs.input.focus()
        }
      })
    },
    onSearchFocus() {
      this.isSearchFocused = true
    },
    onSearchBlur() {
      // Add a small delay to prevent the button from disappearing before click
      setTimeout(() => {
        this.isSearchFocused = false
      }, 100)
    },
    clearSearch() {
      clearTimeout(this.timeout)
      this.runSearch.cancel()
      this.searchQuery = ''
      this.searchResults = []
      this.isLoading = false
      // Keep focus on the input after clearing
      this.$nextTick(() => {
        const searchInput = this.$refs.searchInput
        if (searchInput && searchInput.$refs.input) {
          searchInput.$refs.input.focus()
          this.isSearchFocused = true
        }
      })
    },
    onSearchInput(query) {
      // Clear previous timeout
      clearTimeout(this.timeout)
      if (query && query.length > 0) {
        this.isLoading = true

        // empty search results
        this.searchResults = []
        // Debounce the search
        this.timeout = setTimeout(() => {
          this.fetchResults(query)
        }, 250)
      } else {
        this.runSearch.cancel()
        this.searchResults = []
        this.isLoading = false
      }
    },

    async fetchResults(query) {
      // Called by the debounce in onSearchInput.
      const trimmedQuery = query.trim()
      if (trimmedQuery === '') {
        this.runSearch.cancel()
        this.searchResults = []
        this.isLoading = false
        return
      }

      this.isLoading = true // Set loading to true right before the API call.
      try {
        const result = await this.runSearch(trimmedQuery)
        // A newer search replaced this one; it owns the results and loading state.
        if (result === CANCELLED) return
        const hits = result.result.result.hits || []
        this.searchResults = this.expandInvoiceMatches(hits)
        this.isLoading = false
      } catch (error) {
        console.error('Search error:', error)
        this.searchResults = [] // Clear results on error.
        this.isLoading = false
        this.captureSearchError(error)
      }
    },
    // Report search failures to Sentry so we can see from production whether
    // (and how often) global search is failing — issues are intermittent and
    // not reproducible locally. Never let telemetry break search.
    captureSearchError(error) {
      try {
        Sentry.captureException(error, {
          tags: {
            feature: 'global-search'
          },
          extra: {
            httpStatus: (error && error.httpStatus) || 'N/A'
          }
        })
      } catch (telemetryError) {
        console.error('Failed to report global search error to Sentry:', telemetryError)
      }
    },
    hasHighlightTokenMatch(highlightField) {
      if (!highlightField) return false
      return (
        (highlightField.matched_tokens && highlightField.matched_tokens.length > 0) ||
        (highlightField.snippet && highlightField.snippet.includes('<em>')) ||
        (highlightField.value && highlightField.value.includes('<em>'))
      )
    },
    expandInvoiceMatches(results = []) {
      const expandedResults = []

      results.forEach((item) => {
        if (this.getResultType(item) !== 'invoice') {
          expandedResults.push(item)
          return
        }

        const invoiceHighlights = item?.highlight?.invoices
        if (!Array.isArray(invoiceHighlights)) {
          expandedResults.push(item)
          return
        }

        const sourceInvoices = Array.isArray(item.invoices) ? item.invoices : []

        const matchedIndexes = invoiceHighlights
          .map((invoiceHighlight, index) => ({
            index,
            hasMatch: this.hasHighlightTokenMatch(invoiceHighlight?.no)
          }))
          .filter((entry) => entry.hasMatch)
          .map((entry) => entry.index)

        if (matchedIndexes.length === 0) {
          expandedResults.push(item)
          return
        }

        matchedIndexes.forEach((matchIndex) => {
          const matchedInvoice = sourceInvoices[matchIndex]
          if (!matchedInvoice) return

          expandedResults.push({
            ...item,
            invoices: [matchedInvoice],
            highlight: {
              ...(item.highlight || {}),
              invoices: [invoiceHighlights[matchIndex]]
            }
          })
        })
      })

      return expandedResults
    },
    getResultType(result) {
      // 1. Use highlight first - detects which field actually matched the query.
      const highlight = result.highlight || {}
      const highlightHasMatch = (fields) =>
        fields.some((field) => {
          const h = highlight[field]
          if (!h) return false
          const items = Array.isArray(h) ? h : [h]
          return items.some((item) => this.hasHighlightTokenMatch(item))
        })

      // Quote-specific fields take HIGHEST priority — if the search matched quoteNo or
      // any other quote-identity field, always classify as quote, even if the document
      // also has PO/invoice data (which would cause partial matches on those fields too).
      const quoteFields = [
        'quoteNo',
        'regoNo',
        'customer',
        'makeModel',
        'claimNo',
        'colour',
        'assessorName',
        'insurer',
        'phoneNo',
        'vinNumber',
        'labourType',
        'buildYear',
        'partNumbers'
      ]
      if (highlightHasMatch(quoteFields)) return 'quote'

      // pos highlight is nested: highlight.pos[i].vendor / .no / .pcNo
      const posHighlight = highlight['pos']
      if (Array.isArray(posHighlight) && posHighlight.some((item) =>
        ['no', 'pcNo', 'vendor'].some((subField) => this.hasHighlightTokenMatch(item[subField]))
      )) return 'po'
      if (highlightHasMatch(['invoiceNo', 'invoiceNumber'])) return 'invoice'

      // invoices highlight is nested: highlight.invoices[i].no
      const invoicesHighlight = highlight['invoices']
      if (Array.isArray(invoicesHighlight) && invoicesHighlight.some((item) =>
        this.hasHighlightTokenMatch(item['no'])
      )) return 'invoice'

      // 2. Fallback: field presence on the document (no highlight returned).
      const hasValue = (val) => {
        if (val === null || val === undefined) return false
        if (Array.isArray(val)) return val.length > 0
        if (typeof val === 'string') return val.trim().length > 0
        if (typeof val === 'object') return Object.keys(val).length > 0
        return Boolean(val)
      }

      if (hasValue(result.pos)) return 'po'
      if (hasValue(result.invoices)) return 'invoice'

      return 'quote'
    },
    onSelectResult(selectedOption) {
      if (!selectedOption || !selectedOption.id) return

      const type = this.getResultType(selectedOption)
      const currentQuoteId = this.$route.params?.quoteId
      const isSwitchingQuote = currentQuoteId && currentQuoteId !== selectedOption.id

      if (type === 'invoice') {
        const target = { name: 'QuoteInvoice', params: { quoteId: selectedOption.id } }
        if (isSwitchingQuote) {
          const { href } = this.$router.resolve(target)
          window.location.assign(href)
        } else {
          this.$router.push(target)
        }
      } else if (type === 'po') {
        const target = { name: 'QuotePartsControl', params: { quoteId: selectedOption.id } }
        if (isSwitchingQuote) {
          const { href } = this.$router.resolve(target)
          window.location.assign(href)
        } else {
          this.$router.push(target)
        }
      } else {
        if (isSwitchingQuote) {
          // When switching between quotes, use a full navigation so QuoteView is
          // destroyed and recreated with fresh data rather than updated in-place
          // via beforeRouteUpdate (which leaves vehicle/customer/insurer stale).
          const { href } = this.$router.resolve({ name: 'QuoteHeader', params: { quoteId: selectedOption.id } })
          window.location.assign(href)
        } else {
          this.$router.push({ name: 'QuoteHeader', params: { quoteId: selectedOption.id } })
        }
      }
    },
    getGroupIcon(groupLabel) {
      if (!groupLabel) return 'list_alt'
      const label = groupLabel.toLowerCase()
      if (label.startsWith('quotes')) return 'request_quote'
      if (label.startsWith('po')) return 'shopping_cart'
      if (label.startsWith('invoices')) return 'receipt'
      return 'list_alt'
    }
  }
}
</script>

<style lang="scss" scoped>
@use 'sass:color';
@use '~bulma/sass/utilities/initial-variables' as iv;

.search-container {
  position: relative;
  max-width: 400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.beta-info-panel {
  border-radius: 6px;
  border: 1px solid #3e8ed0;
  margin: 0 auto;

  ul {
    list-style: none;
    padding-left: 0;
  }
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.search-input-wrapper .input {
  flex: 1;
  padding-right: 40px !important;
  padding-left: 40px !important; /* Add left padding for search icon */
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #3273dc; /* Primary color */
  font-size: 18px;
  z-index: 10;
  pointer-events: none;
}

.clear-search-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  font-size: 18px;
  color: #999;
  cursor: pointer;
  padding: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 20;
  transition: all 0.2s ease;
  opacity: 0;
  visibility: hidden;
  background: transparent;

  &:hover {
    color: #666;
    background-color: #f0f0f0;
  }

  .is-dark & {
    color: #9ca3af;

    &:hover {
      color: #e5e7eb;
      background-color: #374151;
    }
  }
}

/* Show clear button when search input is focused and has value */
.search-input-wrapper:focus-within .clear-search-button {
  opacity: 1;
  visibility: visible;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.global-search-autocomplete {
  width: 100%;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  :deep(.input) {
    padding: 0.5rem 2.5rem 0.5rem 2.5rem; /* Left padding for search icon, right padding for clear button */
    font-size: 1rem;
    transition: all 0.3s ease;
    border: none;
    outline: none;

    &:focus,
    &:active {
      box-shadow: none !important;
      border-color: transparent !important;
    }

    &.dark {
      background-color: color.mix(black, iv.$grey, 10%);
    }
  }

  &.light {
    :deep(.input) {
      background-color: color.mix(white, iv.$grey-lighter, 50%);
    }
  }

  &.dark {
    :deep(.input) {
      background-color: color.mix(black, iv.$grey, 10%);
    }
  }

  .dropdown-menu {
    padding-top: 4px !important;
    width: 100%;
  }

  .dropdown-content {
    margin: 0 auto;
  }
}

.autocomplete {
  .dropdown-content {
    overflow-y: auto;
  }
}

.s-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  margin: 0;
  border-top: 1px solid #374151;
  border-bottom: 1px solid #374151;
  background: #16191d;
}

.s-group-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #9ca3af;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.s-group-icon {
  font-size: 16px;
  line-height: 1;
}

/* Ensure the dropdown can be wider than the input */
.global-search-autocomplete >>> .dropdown-content {
  max-width: 90vw;
}
</style>
<style lang="scss">
.global-search-autocomplete {
  .dropdown-menu {
    padding-top: 4px !important;
    left: 50% !important;
    transform: translateX(-50%);
  }
  .dropdown-content {
    max-height: 500px !important;
    overflow-y: auto;
    width: 600px;
    max-width: 90vw;
    padding-top: 4px !important;
    box-shadow: none !important;
    outline: none !important;

    a.dropdown-item,
    div.dropdown-item {
      outline: none !important;
    }
  }
}
</style>